import { ROLE_PERMISSIONS } from "@link/shared";
import type { SpaceMember, SpaceRole } from "./api";
import { knownBadges } from "./badges";
import type { MemberProfile } from "../components/MemberProfilePopover";

/** Lower positions rank higher; tie-breaking is deterministic across clients. */
export function highestMemberRole(member: Pick<SpaceMember, "roles">, roles: SpaceRole[]) {
  const assigned = new Set(member.roles.map((role) => role.xid));
  return roles.filter((role) => assigned.has(role.xid))
    .sort((a, b) => a.sort_position - b.sort_position || a.xid.localeCompare(b.xid))[0];
}

export function memberProfile(member: SpaceMember, spaceXid: string, spaceName?: string): MemberProfile {
  return {
    name: member.nickname ?? member.display_name ?? member.username,
    tag: member.account_id, username: member.username, pronouns: member.pronouns,
    avatarUrl: member.avatar_url, bannerColor: member.banner_color,
    bannerMode: member.banner_mode, bannerSecondaryColor: member.banner_secondary_color,
    status: member.presence, bio: null,
    roles: member.roles.map((role) => ({ id: role.xid, label: role.name, color: role.color })),
    mutualSpaces: spaceName === undefined ? [] : [spaceName], badges: knownBadges(member.badges), communityTag: member.community_tag,
    memberSince: new Date(member.joined_at).toLocaleDateString(), currentSpaceXid: spaceXid,
  };
}

/** UI mirrors the server gates; the API remains the authority for every mutation. */
export function memberActions(actor: SpaceMember | undefined, target: SpaceMember, roles: SpaceRole[], defaults: number) {
  const owner = actor?.is_owner === true;
  const same = actor?.account_id === target.account_id;
  const permissions = owner || actor?.is_admin ? Number.MAX_SAFE_INTEGER
    : roles.filter((role) => actor?.roles.some((r) => r.xid === role.xid))
      .reduce((mask, role) => mask | role.permissions, defaults);
  const rank = (member: SpaceMember) => member.is_owner ? -1 : member.is_admin ? 0
    : highestMemberRole(member, roles)?.sort_position ?? Number.MAX_SAFE_INTEGER;
  const outranks = actor !== undefined && !same && !target.is_owner && (owner || rank(actor) < rank(target));
  const has = (bit: number) => actor !== undefined && (permissions & bit) !== 0;
  const assignableRoles = !has(ROLE_PERMISSIONS.manage_roles) || (!owner && !outranks) ? []
    : roles.filter((role) => owner || (actor !== undefined && role.sort_position > rank(actor) && (role.permissions & ~permissions) === 0));
  return {
    same, assignableRoles,
    editNickname: same ? has(ROLE_PERMISSIONS.change_nickname) : outranks && has(ROLE_PERMISSIONS.manage_nicknames),
    kick: outranks && has(ROLE_PERMISSIONS.kick_members),
    ban: outranks && has(ROLE_PERMISSIONS.ban_members),
    legacyAdmin: owner && !same && !target.is_owner,
  };
}
